import axios from 'axios';
import type {
  MakerBrandProfile,
  MarketplaceModuleRegistryEntry,
  MarketplaceTenantProfile,
  ModuleBillingStatus,
  ModuleCatalog,
  ModuleImpact,
  ModuleImpactDashboard,
  ModuleImpactLinkSample,
  TenantModuleActivation,
  TenantModuleLicense,
} from './types';
import { getPreferredAccessToken } from '@/uptk/core/api/clerkTokenProvider';
import { buildSaasHeaders, resolveSaasContext } from '@/uptk/core/api/saasContext';

type ApiOptions = {
  tenantId?: string;
};

export type PersonalTrial = {
  tenantId: string;
  holder: string | null;
  endDate: string | null;
  daysRemaining: number;
  modules: string[];
};

export async function fetchPersonalTrials(options: ApiOptions): Promise<PersonalTrial[]> {
  const response = await axios.get<PersonalTrial[]>(buildUrl('/v1/licensing/trials'), { headers: await buildHeaders(options) });
  return response.data;
}

export async function savePersonalTrial(options: ApiOptions, target: string, endDate: string, extend = false): Promise<void> {
  const headers = await buildHeaders(options);
  if (extend) {
    await axios.patch(buildUrl(`/v1/licensing/trials/${encodeURIComponent(target)}`), { endDate }, { headers });
  } else {
    await axios.post(buildUrl('/v1/licensing/trials'), { tenantId: target, endDate: endDate || null }, { headers });
  }
}

export async function setTrialEligibility(options: ApiOptions, code: string, trialEligible: boolean): Promise<void> {
  await axios.patch(buildUrl(`/v1/modules/catalog/${encodeURIComponent(code)}/trial-eligibility`), { trialEligible }, { headers: await buildHeaders(options) });
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
};

const normalizePermissionsPayload = (payload: unknown): string[] => {
  if (Array.isArray(payload)) {
    return normalizeStringArray(payload);
  }

  if (!isRecord(payload)) {
    return [];
  }

  const arrayCandidates = [payload.permissions, payload.items, payload.data];
  for (const candidate of arrayCandidates) {
    const normalized = normalizeStringArray(candidate);
    if (normalized.length > 0) {
      return normalized;
    }
  }

  return Object.entries(payload)
    .filter(([, enabled]) => enabled === true)
    .map(([permission]) => permission.trim())
    .filter((permission) => permission.length > 0);
};

const normalizeBillingStatusesPayload = (payload: unknown): ModuleBillingStatus[] => {
  if (Array.isArray(payload)) {
    return payload as ModuleBillingStatus[];
  }

  if (!isRecord(payload)) {
    return [];
  }

  const arrayCandidates = [payload.items, payload.data, payload.statuses];
  for (const candidate of arrayCandidates) {
    if (Array.isArray(candidate)) {
      return candidate as ModuleBillingStatus[];
    }
  }

  return [];
};

const normalizeRegistryPayload = (payload: unknown): MarketplaceModuleRegistryEntry[] => {
  if (Array.isArray(payload)) {
    return payload.filter(isRecord);
  }

  if (!isRecord(payload)) {
    return [];
  }

  const arrayCandidates = [payload.items, payload.data, payload.modules, payload.registry];
  for (const candidate of arrayCandidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter(isRecord);
    }
  }

  return [payload];
};

const buildHeaders = async ({ tenantId }: ApiOptions) => {
  const saasContext = resolveSaasContext({ tenantId });
  const accessToken = await getPreferredAccessToken();

  return {
    ...buildSaasHeaders(saasContext),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };
};

const buildUrl = (path: string) => `/api${path}`;

export async function fetchModuleCatalog(options: ApiOptions): Promise<ModuleCatalog[]> {
  const response = await axios.get<ModuleCatalog[]>(buildUrl('/modules/catalog'), {
    headers: await buildHeaders(options),
  });
  return response.data;
}

export async function fetchCurrentTenant(
  options: ApiOptions
): Promise<MarketplaceTenantProfile | null> {
  const response = await axios.get<MarketplaceTenantProfile | null>(buildUrl('/v1/tenants/current'), {
    headers: await buildHeaders(options),
  });
  return response.data;
}

export async function fetchCurrentPermissions(): Promise<string[]> {
  const response = await axios.get<unknown>(buildUrl('/users/current/permissions'), {
    headers: await buildHeaders({}),
  });
  return normalizePermissionsPayload(response.data);
}

export async function fetchModuleActivations(
  options: ApiOptions
): Promise<TenantModuleActivation[]> {
  const response = await axios.get<TenantModuleActivation[]>(buildUrl('/modules/activations'), {
    headers: await buildHeaders(options),
  });
  return response.data;
}

export async function fetchModuleLicenses(options: ApiOptions): Promise<TenantModuleLicense[]> {
  const response = await axios.get<TenantModuleLicense[]>(buildUrl('/modules/licenses'), {
    headers: await buildHeaders(options),
  });
  return response.data;
}

export async function fetchAllModuleBillingStatuses(
  options: ApiOptions
): Promise<ModuleBillingStatus[]> {
  const response = await axios.get<unknown>(buildUrl('/modules/billing/status'), {
    headers: await buildHeaders(options),
  });
  return normalizeBillingStatusesPayload(response.data);
}

export async function syncModuleCatalog(options: ApiOptions): Promise<void> {
  await axios.post(buildUrl('/modules/catalog/sync'), null, {
    headers: await buildHeaders(options),
  });
}

export async function fetchModuleRegistry(
  options: ApiOptions
): Promise<MarketplaceModuleRegistryEntry[]> {
  const response = await axios.get<unknown>(buildUrl('/modules/registry'), {
    headers: await buildHeaders(options),
  });
  return normalizeRegistryPayload(response.data);
}

/**
 * Human-readable message from a backend error payload ({message, details[]}).
 * The API surfaces the actionable text in `details` (e.g. "Missing active
 * dependencies: finance-portfolio", "License required for module: …") — showing
 * a generic "try again" instead hides the fix from the user (UAT 27.08).
 */
export function describeMarketplaceApiError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown; details?: unknown } | undefined;
    const details = Array.isArray(data?.details)
      ? data.details.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
      : [];
    if (details.length > 0) {
      return details.join(' — ');
    }
    if (
      typeof data?.message === 'string' &&
      data.message.length > 0 &&
      !['Conflict', 'Internal Server Error', 'Bad Request', 'Access Denied'].includes(data.message)
    ) {
      return data.message;
    }
  }
  return fallback;
}

const countOf = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

const recordOf = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

/** Normalise la réponse d'impact : un champ absent (serveur antérieur, réponse partielle) vaut « rien ». */
export const normalizeModuleImpact = (payload: unknown, moduleCode: string): ModuleImpact => {
  const data = recordOf(payload);
  const links = recordOf(data.entityLinks);
  const widgets = recordOf(data.dashboardWidgets);
  return {
    module: typeof data.module === 'string' && data.module.length > 0 ? data.module : moduleCode,
    open: data.open === true,
    dependents: normalizeStringArray(data.dependents),
    missingDependencies: normalizeStringArray(data.missingDependencies),
    entityLinks: {
      count: countOf(links.count),
      samples: Array.isArray(links.samples) ? (links.samples.filter(isRecord) as ModuleImpactLinkSample[]) : [],
    },
    dashboardWidgets: {
      count: countOf(widgets.count),
      dashboards: Array.isArray(widgets.dashboards)
        ? (widgets.dashboards.filter(isRecord) as ModuleImpactDashboard[])
        : [],
    },
    goals: { count: countOf(recordOf(data.goals).count) },
    presets: normalizeStringArray(data.presets),
    permissions: { rolesCount: countOf(recordOf(data.permissions).rolesCount) },
  };
};

/**
 * UPT-2069 (L3) — analyse d'impact d'une (dés)activation, en lecture seule : sert le dialogue d'impact de la
 * Marketplace avant l'action (dépendants bloquants, liens, widgets posés, objectifs, préréglages, droits).
 */
export async function fetchModuleImpact(options: ApiOptions, moduleCode: string): Promise<ModuleImpact> {
  const response = await axios.get<unknown>(buildUrl(`/modules/${encodeURIComponent(moduleCode)}/impact`), {
    headers: await buildHeaders(options),
  });
  return normalizeModuleImpact(response.data, moduleCode);
}

export async function installModule(options: ApiOptions, moduleCode: string) {
  await axios.post(buildUrl(`/modules/${moduleCode}/install`), null, {
    headers: await buildHeaders(options),
  });
}

export async function disableModule(options: ApiOptions, moduleCode: string) {
  await axios.post(buildUrl(`/modules/${moduleCode}/disable`), null, {
    headers: await buildHeaders(options),
  });
}

export async function updateModuleLicense(
  options: ApiOptions,
  moduleCode: string,
  payload: {
    licenseType: string;
    status: string;
    validUntil: string | null;
  }
) {
  if (payload.licenseType === 'trial' && payload.status.toLowerCase() === 'active') {
    const headers = await buildHeaders(options);
    const { data } = await axios.get<{ modules: string[] }>(
      buildUrl(`/v1/licensing/trials/${encodeURIComponent(options.tenantId ?? '')}`), { headers }
    );
    const endDate = payload.validUntil?.split('T')[0] ?? null;
    if (data.modules.length > 0) {
      await axios.patch(buildUrl(`/v1/licensing/trials/${encodeURIComponent(options.tenantId ?? '')}`), { endDate }, { headers });
    } else {
      await axios.post(buildUrl('/v1/licensing/trials'), { tenantId: options.tenantId, endDate }, { headers });
    }
    return;
  }
  await axios.put(buildUrl(`/modules/${moduleCode}/license`), payload, {
    headers: await buildHeaders(options),
  });
}

type CheckoutResponse = {
  sessionId: string;
  url: string;
};

type CheckoutItemPayload = {
  moduleCode?: string;
  priceId?: string;
  quantity?: number;
};

export async function createMarketplaceCheckoutSession(
  options: ApiOptions,
  items: CheckoutItemPayload[]
): Promise<CheckoutResponse> {
  const response = await axios.post<CheckoutResponse>(
    buildUrl('/modules/checkout'),
    {
      items,
    },
    {
      headers: await buildHeaders(options),
    }
  );
  return response.data;
}

type BillingPortalResponse = {
  url: string;
};

export async function createMarketplaceBillingPortalSession(
  options: ApiOptions
): Promise<BillingPortalResponse> {
  const response = await axios.post<BillingPortalResponse>(
    buildUrl('/modules/billing/portal'),
    null,
    {
      headers: await buildHeaders(options),
    }
  );
  return response.data;
}

export async function fetchModuleBillingStatus(
  options: ApiOptions,
  moduleCode: string
): Promise<ModuleBillingStatus> {
  const response = await axios.get<ModuleBillingStatus>(
    buildUrl(`/modules/${moduleCode}/billing/status`),
    {
      headers: await buildHeaders(options),
    }
  );
  return response.data;
}

// UPT-1392: fetch published MAKER brand profile for marketplace display.
// Returns null when the maker has not yet published their brand.
export async function fetchMakerBrandProfile(
  makerTenantId: string
): Promise<MakerBrandProfile | null> {
  try {
    const response = await axios.get<{
      displayName?: string;
      logoLightUrl?: string;
      logoDarkUrl?: string;
      publicSiteUrl?: string;
    }>(buildUrl(`/v1/branding/makers/${makerTenantId}`));
    return {
      displayName: response.data.displayName ?? null,
      logoLightUrl: response.data.logoLightUrl ?? null,
      logoDarkUrl: response.data.logoDarkUrl ?? null,
      publicSiteUrl: response.data.publicSiteUrl ?? null,
    };
  } catch {
    return null;
  }
}
